﻿import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { type ApiError, type SalaryMultiplier } from '@pms/shared';
import { payrollService } from '@/services/payroll.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

function getErrorMessage(e: unknown): string {
  const ax = e as AxiosError<ApiError>;
  return ax.response?.data?.error?.message ?? 'Something went wrong';
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function SalaryMultipliersSection() {
  const queryClient = useQueryClient();
  const [localRates, setLocalRates] = useState<Record<number, { rate: string; is_active: boolean }>>({});

  const { data, isLoading } = useQuery({
    queryKey: ['payroll', 'multipliers'],
    queryFn: () => payrollService.getMultipliers(),
  });

  const sorted = data ? [...data].sort((a, b) => a.day_of_week - b.day_of_week) : [];

  const initRates = (items: SalaryMultiplier[]) => {
    const m: Record<number, { rate: string; is_active: boolean }> = {};
    items.forEach((i) => { m[i.day_of_week] = { rate: i.rate, is_active: i.is_active }; });
    return m;
  };

  const rates =
    Object.keys(localRates).length > 0 ? localRates : data ? initRates(data) : {};

  const save = useMutation({
    mutationFn: () =>
      payrollService.updateMultipliers({
        multipliers: sorted.map((m) => ({
          day_of_week: m.day_of_week,
          rate: parseFloat(rates[m.day_of_week]?.rate ?? m.rate),
          is_active: rates[m.day_of_week]?.is_active ?? m.is_active,
        })),
      }),
    onSuccess: () => {
      toast.success('Multipliers saved');
      void queryClient.invalidateQueries({ queryKey: ['payroll', 'multipliers'] });
    },
    onError: (e: unknown) => toast.error(getErrorMessage(e)),
  });

  const updateRate = (dow: number, rate: string) =>
    setLocalRates((prev) => ({ ...prev, [dow]: { ...prev[dow]!, rate } } as Record<number, { rate: string; is_active: boolean }>));

  const toggleActive = (dow: number, is_active: boolean) =>
    setLocalRates((prev) => ({ ...prev, [dow]: { ...prev[dow]!, is_active } } as Record<number, { rate: string; is_active: boolean }>));

  const resetDay = (dow: number) => {
    const orig = data?.find((m) => m.day_of_week === dow);
    if (orig) {
      setLocalRates((prev) => ({
        ...prev,
        [dow]: { rate: orig.rate, is_active: orig.is_active },
      }));
    }
  };

  if (isLoading) {
    return (
      <div className="rounded-lg border border-border/60 bg-card px-4 py-8 text-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border/60 bg-card overflow-hidden">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border/40 px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold">Day-wise Salary Multipliers</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Set the pay rate multiplier for each day. Sunday is typically 2× for overtime/holiday pay.
          </p>
        </div>
      </div>

      {/* Day rows */}
      <div>
        {sorted.map((m) => {
          const r = rates[m.day_of_week] ?? { rate: m.rate, is_active: m.is_active };
          return (
            <div
              key={m.day_of_week}
              className="flex items-center gap-3 border-b border-border/40 px-4 py-3 last:border-0"
            >
              <span className="w-28 text-sm font-medium">{DAY_NAMES[m.day_of_week]}</span>
              <div className="flex items-center gap-1">
                <Input
                  type="number"
                  step="0.25"
                  min="0"
                  max="10"
                  value={r.rate}
                  onChange={(e) => updateRate(m.day_of_week, e.target.value)}
                  className="h-8 w-20 text-right text-sm"
                />
                <span className="text-xs text-muted-foreground">×</span>
              </div>
              <label className="flex cursor-pointer items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={r.is_active}
                  onChange={(e) => toggleActive(m.day_of_week, e.target.checked)}
                  className="rounded"
                />
                <span className="text-xs text-muted-foreground">Active</span>
              </label>
              <button
                onClick={() => resetDay(m.day_of_week)}
                className="ml-auto text-xs text-muted-foreground hover:text-foreground"
                title="Reset to saved"
              >
                Reset
              </button>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="border-t border-border/40 px-4 py-3">
        <Button onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
}
